import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { AwardApiService } from '../../../core/api-services/award-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import {
  AwardStatus,
  GrantCycleAwardsResult,
  ScholarshipAwardDetail,
  ScholarshipAwardSummary,
} from '../../../core/models/scholarship-award';
import { Scholarship } from '../../../core/models/scholarship';

/**
 * Administrator console for student scholarship awards.
 *
 * Awards are created automatically when an application is approved ("approved"
 * means "awarded"); this screen shows the resulting funding records, lets an
 * administrator change an award's status, and awards a whole cycle of approved
 * applications in one run.
 */
@Component({
  selector: 'app-admin-awards',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './awards.html',
  styleUrl: './awards.scss',
})
export class AdminAwardsComponent implements OnInit {
  private readonly api = inject(AwardApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly fb = inject(FormBuilder);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  readonly statuses: AwardStatus[] = ['ACTIVE', 'ON_HOLD', 'COMPLETED', 'TERMINATED'];

  readonly awards = signal<ScholarshipAwardSummary[]>([]);
  readonly scholarships = signal<Scholarship[]>([]);
  readonly selected = signal<ScholarshipAwardDetail | null>(null);
  readonly cycleResult = signal<GrantCycleAwardsResult | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');

  readonly filters = this.fb.group({
    cycle: [''],
    scholarshipId: [null as number | null],
    status: [''],
    search: [''],
  });

  readonly statusForm = this.fb.group({
    status: ['COMPLETED' as AwardStatus],
    reason: ['', Validators.required],
  });

  readonly cycleForm = this.fb.group({
    cycle: [String(new Date().getFullYear()), Validators.required],
    startDate: ['', Validators.required],
    endDate: ['', Validators.required],
    scholarshipId: [null as number | null],
    currency: ['ZAR'],
  });

  ngOnInit(): void {
    this.load();
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => this.scholarships.set([]),
    });
  }

  load(): void {
    const value = this.filters.getRawValue();
    this.loading.set(true);
    this.error.set('');

    this.api
      .getAwardsForReview(
        value.cycle || null,
        value.scholarshipId ?? null,
        (value.status as AwardStatus) || null,
        value.search ?? ''
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (awards) => this.awards.set(awards ?? []),
        error: () => this.error.set('The award list could not be loaded.'),
      });
  }

  open(award: ScholarshipAwardSummary): void {
    this.error.set('');
    this.message.set('');

    this.api.getAward(award.id).subscribe({
      next: (detail) => {
        this.selected.set(detail);
        this.statusForm.reset({
          status: detail.status === 'ACTIVE' ? 'COMPLETED' : detail.status,
          reason: '',
        });
      },
      error: () => this.error.set('This award could not be loaded.'),
    });
  }

  close(): void {
    this.selected.set(null);
  }

  /** Completes, terminates, holds or reactivates an award. */
  changeStatus(): void {
    const award = this.selected();
    if (!award || this.busy()) {
      return;
    }

    const value = this.statusForm.getRawValue();
    const closure = value.status === 'COMPLETED' || value.status === 'TERMINATED';

    if (closure && !(value.reason ?? '').trim()) {
      this.statusForm.markAllAsTouched();
      this.error.set('A reason is required when completing or terminating an award.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .changeStatus(award.id, { status: value.status!, reason: value.reason ?? null })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.selected.set(updated);
          this.message.set(
            `Award ${updated.referenceNumber} is now ${updated.status}.` +
              (closure
                ? ' The student is promoted to alumni once their last open award closes.'
                : '')
          );
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The award status could not be changed.'),
      });
  }

  /** Awards every approved application for the cycle. Dry run previews first. */
  grantCycle(dryRun: boolean): void {
    if (this.cycleForm.invalid || this.busy()) {
      this.cycleForm.markAllAsTouched();
      return;
    }

    const value = this.cycleForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.cycleResult.set(null);

    this.api
      .grantCycleAwards({
        cycle: value.cycle!,
        startDate: value.startDate!,
        endDate: value.endDate!,
        scholarshipId: value.scholarshipId ?? null,
        currency: value.currency ?? 'ZAR',
        dryRun,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.cycleResult.set(result);
          this.message.set(
            dryRun
              ? `Preview: ${result.awarded} award(s) would be created from ${result.considered} approved application(s).`
              : `${result.awarded} award(s) created from ${result.considered} approved application(s).`
          );

          if (!dryRun) {
            this.load();
          }
        },
        error: (err) => this.error.set(err?.error?.message || 'The cycle award run failed.'),
      });
  }

  money(value: number | null | undefined, currency: string | null | undefined): string {
    return `${(value ?? 0).toFixed(2)} ${currency ?? ''}`.trim();
  }

  progressWidth(percent: number | null | undefined): string {
    return `${Math.min(100, Math.max(0, percent ?? 0))}%`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'status status-approved';
      case 'COMPLETED':
        return 'status status-booked';
      case 'TERMINATED':
        return 'status status-rejected';
      default:
        return 'status status-pending';
    }
  }
}
